import { useCallback, useEffect, useRef, useState } from 'react';

const SWIPE_THRESHOLD = 56;
const HORIZONTAL_DOMINANCE = 1.25;
const TRANSITION_MS = 340;
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, label, [role="button"]';

function isInteractiveTarget(target) {
    return target instanceof Element && Boolean(target.closest(INTERACTIVE_SELECTOR));
}

export function usePontoSlideTransition({
    onNavigateNext,
    onNavigatePrev,
    enabled = true,
    pontoId,
}) {
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [exitDirection, setExitDirection] = useState(null);
    const [enterDirection, setEnterDirection] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const startRef = useRef({ x: 0, y: 0 });
    const activeRef = useRef(false);
    const horizontalRef = useRef(false);
    const capturedRef = useRef(false);
    const pendingEnterRef = useRef(null);
    const transitionTimerRef = useRef(null);

    const clearTransitionTimer = useCallback(() => {
        if (transitionTimerRef.current) {
            clearTimeout(transitionTimerRef.current);
            transitionTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearTransitionTimer(), [clearTransitionTimer]);

    useEffect(() => {
        if (!pendingEnterRef.current) return;

        const direction = pendingEnterRef.current;
        pendingEnterRef.current = null;
        setEnterDirection(direction);

        const timer = setTimeout(() => setEnterDirection(null), TRANSITION_MS);
        return () => clearTimeout(timer);
    }, [pontoId]);

    const releaseCapture = useCallback((target, pointerId) => {
        if (!capturedRef.current || !target) return;
        try {
            target.releasePointerCapture(pointerId);
        } catch {
            // pointer já liberado
        }
        capturedRef.current = false;
    }, []);

    const resetDrag = useCallback(() => {
        activeRef.current = false;
        horizontalRef.current = false;
        capturedRef.current = false;
        setIsDragging(false);
        setDragOffset(0);
    }, []);

    const runTransition = useCallback((direction, navigateFn) => {
        if (!enabled || isTransitioning || !navigateFn) return;

        clearTransitionTimer();
        setIsTransitioning(true);
        setExitDirection(direction);
        resetDrag();

        transitionTimerRef.current = setTimeout(() => {
            pendingEnterRef.current = direction;
            navigateFn();
            setExitDirection(null);
            setIsTransitioning(false);
        }, TRANSITION_MS);
    }, [clearTransitionTimer, enabled, isTransitioning, resetDrag]);

    const goNext = useCallback(() => {
        runTransition('next', onNavigateNext);
    }, [onNavigateNext, runTransition]);

    const goPrev = useCallback(() => {
        runTransition('prev', onNavigatePrev);
    }, [onNavigatePrev, runTransition]);

    const handleStart = useCallback((clientX, clientY) => {
        if (!enabled || isTransitioning) return;
        startRef.current = { x: clientX, y: clientY };
        activeRef.current = true;
        horizontalRef.current = false;
        setIsDragging(true);
    }, [enabled, isTransitioning]);

    const handleMove = useCallback((clientX, clientY, preventDefault) => {
        if (!activeRef.current || !enabled || isTransitioning) return;

        const dx = clientX - startRef.current.x;
        const dy = clientY - startRef.current.y;

        if (!horizontalRef.current) {
            if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
            if (Math.abs(dx) > Math.abs(dy) * HORIZONTAL_DOMINANCE) {
                horizontalRef.current = true;
            } else {
                activeRef.current = false;
                setIsDragging(false);
                return;
            }
        }

        preventDefault?.();
        const resistance = 0.55;
        const max = 140;
        const damped = Math.max(-max, Math.min(max, dx * resistance));
        setDragOffset(damped);
    }, [enabled, isTransitioning]);

    const handleEnd = useCallback((clientX) => {
        if (!activeRef.current || !enabled) {
            resetDrag();
            return;
        }

        const dx = clientX - startRef.current.x;
        if (horizontalRef.current) {
            if (dx <= -SWIPE_THRESHOLD) goNext();
            else if (dx >= SWIPE_THRESHOLD) goPrev();
            else resetDrag();
        } else {
            resetDrag();
        }

        activeRef.current = false;
        horizontalRef.current = false;
        setIsDragging(false);
    }, [enabled, goNext, goPrev, resetDrag]);

    const bindSwipe = useCallback(() => ({
        onPointerDown: (event) => {
            if (event.button !== 0 || isTransitioning || isInteractiveTarget(event.target)) return;
            handleStart(event.clientX, event.clientY);
        },
        onPointerMove: (event) => {
            if (!activeRef.current) return;

            const wasHorizontal = horizontalRef.current;
            handleMove(event.clientX, event.clientY, () => event.preventDefault());

            if (!wasHorizontal && horizontalRef.current && !capturedRef.current) {
                try {
                    event.currentTarget.setPointerCapture(event.pointerId);
                    capturedRef.current = true;
                } catch {
                    // captura indisponível
                }
            }
        },
        onPointerUp: (event) => {
            handleEnd(event.clientX);
            releaseCapture(event.currentTarget, event.pointerId);
        },
        onPointerCancel: (event) => {
            releaseCapture(event.currentTarget, event.pointerId);
            resetDrag();
        },
    }), [handleStart, handleMove, handleEnd, isTransitioning, releaseCapture, resetDrag]);

    return {
        dragOffset,
        isDragging,
        exitDirection,
        enterDirection,
        isTransitioning,
        goNext,
        goPrev,
        bindSwipe,
    };
}
